import { compact } from '@direct-frontend/stdlib';
import { i18n } from './validateRangeFilterExpression.i18n';
import { FilterItem } from '../../../lib/types/Filters';

/**
 * Функция для валидации значений в фильтре выражении
 * @typeParam filterItem значение фильтра
 * @typeParam index индекс значения фильтра
 * @typeParam filterItemsByFilterId все выбранные фильтры
 * @returns список ошибок или null
 * @kind helper
 */
export function validateRangeFilterExpression<FilterId extends string, ValueType extends string>(
    filterItem: FilterItem<FilterId, ValueType>,
    index: number,
    filterItemsByFilterId: FilterItem<FilterId, ValueType>[]
): { errors: string[] } | null {
    const { filterId, subFilterId, operator, value } = filterItem;

    if (value !== '' && Number.isNaN(Number(value))) {
        return { errors: [i18n('Неправильный формат')] };
    }

    if (filterItemsByFilterId.length === 1) {
        return null;
    }

    const errors = compact(
        filterItemsByFilterId.map(
            (
                { filterId: filterIdItem, subFilterId: subFilterIdItem, operator: operatorItem, value: valueItem },
                indexItem
            ) => {
                // проверка на дублирующиеся условия
                if (
                    indexItem !== index &&
                    filterIdItem === filterId &&
                    subFilterIdItem === subFilterId &&
                    operator === operatorItem &&
                    value === valueItem &&
                    value !== ''
                ) {
                    return i18n('Повторяющиеся условия');
                } else if (
                    filterIdItem === filterId &&
                    subFilterIdItem === subFilterId &&
                    valueItem !== '' &&
                    value !== ''
                ) {
                    switch (operator) {
                        case '=':
                            // проверка, что не выбрано несколько условий равенства, например f1 = 12 и f2 = 15
                            if (operator === operatorItem && value !== '' && indexItem !== index) {
                                return i18n('Указано несколько условий равенства');
                            }
                            // проверка, что не выбрано равенство и неравенство c одинаковыми значениями, например f1 = 12 и f2 != 12
                            return Number(value) === Number(valueItem) && operatorItem === '!='
                                ? i18n('Указано несколько противоречащих условий')
                                : null;
                        case '!=':
                            // проверка, что не выбрано равенство и неравенство c одинаковыми значениями, например f1 = 12 и f2 != 12
                            return Number(value) === Number(valueItem) && operatorItem === '='
                                ? i18n('Указано несколько противоречащих условий')
                                : null;
                        case '<=':
                            if (operator === operatorItem) {
                                // проверка, что не выбраны условия с одинаковыми операторами и значения которых пересекаются, например f1 <= 12 и f2 <= 16
                                return Number(value) > Number(valueItem)
                                    ? i18n('Указано несколько условий одинакого типа')
                                    : null;
                            } else if (operatorItem === '>=') {
                                // проверка, что не выбраны условия с разными операторами и значения которых не могут пересекаться, например f1 >= 12 и f2 <= 9
                                return Number(value) < Number(valueItem)
                                    ? i18n('Указано несколько противоречащих условий')
                                    : null;
                                // любой оператор после указанного равенства будет с ошибкой, например f1 = 12 и f2 <= 9
                            } else if (operatorItem === '=') {
                                return i18n('Указано несколько противоречащих условий');
                            }
                            return false;
                        case '>=':
                            if (operator === operatorItem) {
                                // проверка, что не выбраны условия с одинаковыми операторами и значения которых пересекаются, например f1 >= 12 и f2 >= 11
                                return Number(value) < Number(valueItem)
                                    ? i18n('Указано несколько условий одинакого типа')
                                    : null;
                            } else if (operatorItem === '<=') {
                                // проверка, что не выбраны условия с разными операторами и значения которых не могут пересекаться, например f1 <= 12 и f2 >= 15
                                return Number(value) > Number(valueItem)
                                    ? i18n('Указано несколько противоречащих условий')
                                    : null;
                            } else if (operatorItem === '=') {
                                // любой оператор после указанного равенства будет с ошибкой, например f1 = 12 и f2 >= 9
                                return i18n('Указано несколько противоречащих условий');
                            }
                            return false;
                        default:
                            return false;
                    }
                }
                return false;
            }
        )
    );
    return errors.length > 0 ? { errors: [errors[0] ?? i18n('Неправильный формат')] } : null;
}
